// ----------------------------------------------------------------------------
// -------------------------------------------------------------------- Imports
// ----------------------------------------------------------------------------
// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Libraries
import React from "react";
// import PropTypes from 'prop-types';
import { css } from "glamor";
import { Helmet } from "react-helmet";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Components
import { Elements } from "@bodhi-project/typography";
import { OutLink, Image } from "@bodhi-project/components";
import { Form, Select, Input, Button, notification } from "antd";
import Recaptcha from "react-recaptcha";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Locals
import DefaultSection from "../components/DefaultSection";
import { formStyleClass } from "../styles/defaultFormStyles";
import { validateEmail, hasErrors } from "../helpers/formHelpers";
import docbrij from "../assets/docbrij.jpg";

// ~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~ Abstractions
const { H1, H2, H3, Paragraph } = Elements;
const FormItem = Form.Item;
const { Option } = Select;
const { TextArea } = Input;

// ----------------------------------------------------------------------------
// ------------------------------------------------------------------ Functions
// ----------------------------------------------------------------------------

// ----------------------------------------------------------------------------
// --------------------------------------------------------------------- Styles
// ----------------------------------------------------------------------------
const blockStyle = css({
  backgroundImage:
    "radial-gradient(73% 147%, #c32148 10%, #edbcc8 80%), radial-gradient(91% 146%, rgba(255,255,255,0.50) 47%, rgba(0,0,0,0.50) 100%)",
  backgroundBlendMode: "screen",
  minHeight: "100vh",
  alignItems: "top",

  "@media(max-width: 768px)": {
    "& #lead": {
      marginTop: "4vh"
    }
  },

  "& #lead": {
    marginTop: "12vh",
    marginBottom: 10
  },

  "& #hero": {
    marginBottom: 10
  },

  "& home": {
    marginTop: "12vh",
    marginBottom: 10
  }
});
const blockStyleClass = blockStyle.toString();

// ----------------------------------------------------------------------------
// ------------------------------------------------------------------ Component
// ----------------------------------------------------------------------------
/** Contact Form */
class ContactForm extends React.Component {
  /** standard constructor */
  constructor(props) {
    super(props);

    this.state = {
      loader: null,
      formSent: false,
      geo: null,
      isVerified: false
    };

    this.handleSubmit = this.handleSubmit.bind(this);
    this.handleSubscribe = this.handleSubscribe.bind(this);
    this.recaptchaLoaded = this.recaptchaLoaded.bind(this);
    this.verifyCallback = this.verifyCallback.bind(this);
  }

  recaptchaLoaded() {
    console.log("capcha successfully loaded");
  }

  handleSubscribe() {
    if (this.state.isVerified) {
      alert("You have successfully subscribed!");
    } else {
      alert("Please verify that you are a human!");
    }
  }

  verifyCallback(response) {
    console.log("response" + response);
    if (response) {
      this.setState({
        isVerified: true
      });
    }
  }

  /** On load get IP details */
  componentDidMount() {
    // To disabled submit button at the beginning.
    this.props.form.validateFields();

    fetch("https://geoip.nekudo.com/api")
      .then(response => response.json())
      .then(json => {
        this.setState({
          geo: {
            ip: json.ip,
            country: `${json.country.name} (${json.country.code})`,
            city: json.city ? json.city : "NA",
            latLon: `${json.location.latitude}, ${json.location.longitude}`,
            pin: `http://maps.google.com/maps?q=${json.location.latitude},${
              json.location.longitude
            }`,
            timeZone: json.location.time_zone
          }
        });
      })
      .catch(error => {
        console.log(error);
      });
  }

  /** Submit form data */
  handleSubmit(e) {
    e.preventDefault();
    /** Check ReCaptch is verified or not */
    if (this.state.isVerified) {
      this.props.form.validateFields((err, values) => {
        if (!err) {
          // console.log('Received values of form: ', values);
          this.setState({
            // Show loader and reset errors if any.
            loader: true
          });

          setTimeout(() => {
            // Mock some delay
            fetch(
              "https://hooks.slack.com/services/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",
              {
                method: "POST",
                headers: {
                  Accept: "application/json"
                },
                body: JSON.stringify({
                  attachments: [
                    {
                      fallback:
                        "Someone filled the contact form @ <https://dmr.pub/contact|https://dmr.pub/contact>",
                      pretext:
                        "Someone filled the contact form @ <https://dmr.pub/contact|https://dmr.pub/contact>",
                      color: "#DEAE4D",
                      fields: [
                        {
                          title: "Name",
                          value: values.name,
                          short: false
                        },
                        {
                          title: "Email",
                          value: values.email,
                          short: false
                        },
                        {
                          title: "Message",
                          value: values.concern,
                          short: false
                        },
                        {
                          title: "Location (Approximation)",
                          value: `IP: ${this.state.geo.ip}\nCountry: ${
                            this.state.geo.country
                          }\nRegion: ${this.state.geo.region}\nCity: ${
                            this.state.geo.city
                          }\nLat,Lon: ${this.state.geo.latLon}\nPin: <${
                            this.state.geo.pin
                          }|${this.state.geo.pin}>\nTimezone: ${
                            this.state.geo.timeZone
                          }`,
                          short: false
                        }
                      ]
                    }
                  ]
                })
              }
            )
              .then(() => {
                this.setState({
                  loader: false,
                  formSent: true
                });
              })
              .catch(error => {
                console.log(error);
                this.setState({
                  loader: false
                });
              });
          }, 1500);
        }
      });
    } else {
      /** If ReCaptchs is not verified */
      console.log("Not Varified...");
      notification.error({
        message: "ReCaptcha is required field",
        description: "Please verify that you are not a robot.",
        placement: "bottomLeft"
      });
    }
  }

  /** standard renderer */
  render() {
    const {
      getFieldDecorator,
      getFieldsError,
      getFieldError,
      isFieldTouched
    } = this.props.form;
    // Only show error after a field is touched.
    const nameError = isFieldTouched("name") && getFieldError("name");
    const emailError = isFieldTouched("email") && getFieldError("email");
    const formatError = isFieldTouched("format") && getFieldError("format");
    const languageError = isFieldTouched("reason") && getFieldError("language");
    const outputError = isFieldTouched("output") && getFieldError("output");
    const concernError = isFieldTouched("concern") && getFieldError("concern");

    return (
      <DefaultSection className={blockStyleClass}>
        <H1 id="lead">Request for Information</H1>
        <Paragraph id="hero" style={{ maxWidth: "40em" }}>
          <i>
            Thanks for reaching out to us. Please write to us and we shall reply
            at the earliest. Thank you!
          </i>
        </Paragraph>

        <H2 style={{ marginTop: "5vh" }}>Contacts</H2>
        <Paragraph style={{ marginBottom: "10vh" }}>
          Information: <a href="mailto:hello@dmi.systems">hello@dmi.systems</a>
        </Paragraph>
        <Paragraph style={{ marginBottom: "10vh" }}>
          Tech Support:{" "}
          <a href="mailto:techsupport@dmi.systems">techsupport@dmi.systems</a>
        </Paragraph>
        <Paragraph style={{ marginBottom: "10vh" }}>
          Accounts:{" "}
          <a href="mailto:techsupport@dmi.systems">accounts@dmi.systems</a>
        </Paragraph>
        <Paragraph style={{ marginBottom: "10vh" }}>
          Call (USA): +1 609 284 9544
        </Paragraph>
      </DefaultSection>
    );
  }
}

const WrappedForm = Form.create()(ContactForm);

// ----------------------------------------------------------------------------
// -------------------------------------------------------------------- Exports
// ----------------------------------------------------------------------------
export default WrappedForm;



// WEBPACK FOOTER //
// ./src/blocks/ContactForm.jsx